import * as Flows from './types.no-deps'
import * as functionalArray from '../../lib/functional/array.no-deps'
import { hasShowIfs, isGuideElement, isLoopElement, isSectionElement } from './typeGuards.no-deps'
import { isVisible } from './summary.no-deps'
import {
    sectionsElements,
    stepsElements,
    walkElements,
    walkElementsOrderedByDisplay,
    type TraverseOptions,
} from './generators.no-deps'
import { filter, find, some, toArray } from '../../lib/functional/generator.no-deps'
import { pipe } from '../../lib/functional/pipe.no-deps'
import type { LoopPathIds } from './LoopPath.no-deps'

export type ElementOrNested = Flows.Element | Flows.NestedElement

const hasId = (elementId: Flows.ElementId) => (element: ElementOrNested) => element.id === elementId

export function findElement(section: Flows.HasSteps, elementId: Flows.ElementId): ElementOrNested {
    return pipe(section, walkElements(), find(hasId(elementId)))
}

export function findElementInSection(section: Flows.HasSteps, elementId: Flows.ElementId): ElementOrNested {
    return pipe(section, sectionsElements(), find(hasId(elementId)))
}

export function getAllElements(section: Flows.HasSteps, opts?: TraverseOptions): ElementOrNested[] {
    return pipe(section, walkElements(opts), toArray)
}

export function getAllElementsOrderedByDisplay(section: Flows.HasSteps, opts?: TraverseOptions): ElementOrNested[] {
    return pipe(section, walkElementsOrderedByDisplay(opts), toArray)
}

export function getSectionsElements(section: Flows.HasSteps, opts?: TraverseOptions): ElementOrNested[] {
    return pipe(section, sectionsElements(opts), toArray)
}

export function getVisibleElementsInStep(
    step: Flows.Step,
    flowSummary: Flows.FlowSummary,
    loopPathIds: LoopPathIds
): ElementOrNested[] {
    return pipe(
        step,
        stepsElements, // TODO extend loopPathIds as we iterate through loops
        filter((element) => isVisible(flowSummary, element, loopPathIds)),
        toArray
    )
}

type ElementTransformer = <ElementLike extends Flows.NestedElement>(element: ElementLike) => Flows.NestedElement

/**
 * N.B. Does not map over section or loop elements, just their steps
 */
export function mapElements<SectionLike extends { steps: Flows.Step[] }>(
    section: SectionLike,
    fn: ElementTransformer
): SectionLike {
    const steps = section.steps.map((step) => {
        if (!Flows.isConcreteStep(step)) {
            return step
        }

        const elements = step.elements.map((element) => {
            if (element.type === 'section' || element.type === 'loop') {
                return mapElements(element, fn)
            }

            if (hasShowIfs(element)) {
                return {
                    ...fn(element),
                    showIfs: element.showIfs?.map((showIf) => ({
                        ...showIf,
                        elements: showIf.elements.map(fn),
                    })),
                }
            }

            return fn(element)
        })

        return { ...step, elements }
    })

    return { ...section, steps }
}

/**
 * N.B. Does not map over section or loop elements, just their steps
 */
export function mapTemplateElements<SectionLike extends Flows.Flow>(
    section: SectionLike,
    fn: ElementTransformer
): SectionLike {
    const templates = section?.templates?.map((stepTemplate) => {
        if (!Flows.isConcreteStep(stepTemplate.step)) {
            return stepTemplate
        }

        const elements = stepTemplate.step.elements.map((element) => {
            if (element.type === 'section' || element.type === 'loop') {
                return mapElements(element, fn)
            }

            if (hasShowIfs(element)) {
                return {
                    ...fn(element),
                    showIfs: element.showIfs?.map((showIf) => ({
                        ...showIf,
                        elements: showIf.elements.map(fn),
                    })),
                }
            }

            return fn(element)
        })

        return { ...stepTemplate, step: { ...stepTemplate.step, elements } }
    })

    return { ...section, templates }
}

const hasType = (elementType: ElementOrNested['type']) => (element: ElementOrNested) => element.type === elementType

export function hasElement(section: Flows.Section, elementType: ElementOrNested['type']): boolean {
    return pipe(section, walkElements(), some(hasType(elementType)))
}

export function getLoopPathDepthForElement(section: Flows.HasSteps, elementId: Flows.ElementId): number {
    for (const element of walkElements({ traverseLoops: false })(section)) {
        if (element.id === elementId) return 0
        if (element.type === 'loop') {
            const depth = getLoopPathDepthForElement(element, elementId)
            if (depth === -1) continue
            return 1 + depth
        }
    }
    return -1
}

export function getGuideElements(flow: Flows.Flow): Flows.GuideElement[] {
    return getAllElements(flow).filter(isGuideElement)
}

/**
 * Helper function to return guide ids for all guide elements associated to flow.
 *
 * @param {Flows.Flow} flow
 * @param {boolean} requiredOnly - Filters out non-mandatory guide elements
 */
export function getGuideElementIds(flow: Flows.Flow, requiredOnly = true) {
    const guideElements: Flows.GuideElement[] = getGuideElements(flow)

    return pipe(
        guideElements,
        requiredOnly
            ? functionalArray.filter((guideElement) => guideElement.required && !!guideElement.guideId)
            : functionalArray.filter((guideElement) => !!guideElement.guideId),
        functionalArray.mapField('guideId'),
        functionalArray.uniq
    )
}

export function getDynamicGuideElementIds(flow: Flows.Flow): Flows.GuideElement[] {
    const guideElements: Flows.GuideElement[] = getGuideElements(flow).filter((element) => element.dynamicGuideSettings)
    return guideElements
}

export function getGuideElementTagIdsFromSection(section: Flows.HasSteps): number[] {
    return getAllElements(section, {
        traverseLoops: true,
    })
        .filter((element) => isGuideElement(element) && element.dynamicGuideSettings)
        .map((element: Flows.GuideElement) => element.dynamicGuideSettings.tagId)
}

/**
 * Helper function to remove provided element types from list of steps recursively.
 *
 * @param {Flows.Step[]} flowSteps
 * @param {Flows.ElementType[]} elementTypes
 * @returns {Flows.Step[]}
 */
export function removeElementTypeFromSteps(flowSteps: Flows.Step[], elementTypes: Flows.ElementType[]): Flows.Step[] {
    const updatedSteps: Flows.Step[] = flowSteps.map((step) => {
        if (!Flows.isConcreteStep(step)) {
            return step
        }

        return {
            ...step,
            elements: step.elements.reduce((acc: Flows.Element[], element: Flows.Element) => {
                // Remove elements of provided element types
                // from loop element & section element steps recursively
                // as they can contain any type of elements inside thier steps
                if (
                    (isLoopElement(element) && !elementTypes.includes('loop')) ||
                    (isSectionElement(element) && !elementTypes.includes('section'))
                ) {
                    const updatedElementSteps = removeElementTypeFromSteps(element.steps, elementTypes)
                    acc.push({
                        ...element,
                        steps: updatedElementSteps,
                    })
                    return acc
                }

                if (!elementTypes.includes(element.type)) {
                    acc.push(element)
                }

                return acc
            }, []),
        }
    })

    return updatedSteps
}

export function flatMapElementAncestries<T>(
    flow: Flows.Flow,
    fn: (current: ElementOrNested, ancestors: ElementOrNested[]) => T
): T[] {
    const opts = { traverseLoops: false }
    const forElement = (element: ElementOrNested, stack: ElementOrNested[]): T[] => {
        const results = [fn(element, stack)]
        if (element.type === 'loop' || element.type === 'section') {
            const children = getSectionsElements(element, opts)
            results.push(...children.map((e) => forElement(e, [element, ...stack])).flat())
        }
        return results
    }

    return getSectionsElements(flow, opts)
        .map((e) => forElement(e, []))
        .flat()
}
